import { liveQuery, type Subscription } from "dexie";
import { DateTime } from "luxon";

import { AudioRecordingRepository } from "@/domains/dexie-db/repositories";
import type {
  AudioRecordingMetadata,
  AudioRecordingDeviceInfo,
} from "@/domains/dexie-db/models/audio-recording";
import {
  AudioRecordingCoordinatorDeps,
  AudioRecordingObserver,
  AudioRecordingSnapshot,
  AudioRecordingStatus,
  StartAudioRecordingCommand,
  AudioRecordingProgressUpdate,
  AudioRecordingUploadUpdate,
  AudioRecordingTransformUpdate,
  AudioRecordingAutoStopReason,
} from "./types";
import type { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";

const defaultDeps: AudioRecordingCoordinatorDeps = {
  now: () => DateTime.now().toMillis(),
};

const createDefaultDeviceInfo = (): AudioRecordingDeviceInfo => ({
  activeDevices: [],
  activeLabel: null,
  defaultInputId: null,
});

const createDefaultUploadState = (now: number) => ({
  state: "NOT_STARTED",
  progressPercent: null,
  error: null,
  updatedAt: now,
});

const createDefaultTransformState = (now: number) => ({
  state: "NOT_STARTED",
  error: null,
  updatedAt: now,
});

type RecordingIdentifier = { recordingId: string };
type RecordingProgressArgs = { recordingId: string; progress: AudioRecordingProgressUpdate };
type AppendAudioChunkArgs = { recordingId: string; blob: Blob };
type AppendTranscriptTurnArgs = {
  recordingId: string;
  channel: number;
  transcript: TimestampedAssemblyAITurnMessage;
};
type AppendTranscriptSnapshotArgs = {
  recordingId: string;
  channel: number;
  transcripts: TimestampedAssemblyAITurnMessage[];
  snapshotTime: number;
};
type UpdateUploadStateArgs = { recordingId: string; update: AudioRecordingUploadUpdate };
type UpdateTransformStateArgs = { recordingId: string; update: AudioRecordingTransformUpdate };
type UpdateSilenceArgs = { recordingId: string; silenceSince: number | null };
type SetAutoStopReasonArgs = { recordingId: string; reason: AudioRecordingAutoStopReason | null };
type UpdateDeviceInfoArgs = { recordingId: string; deviceInfo: AudioRecordingDeviceInfo };

export class AudioRecordingCoordinator {
  constructor(
    private readonly repository: AudioRecordingRepository,
    private readonly deps: AudioRecordingCoordinatorDeps = defaultDeps
  ) {}

  async startRecording({
    recordingId,
    noteId,
    capturedAudioId,
    engine,
    isRecordingWithoutTranscription,
    deviceInfo,
    createdAt,
  }: StartAudioRecordingCommand): Promise<AudioRecordingSnapshot | null> {
    const now = createdAt ?? this.deps.now();
    const runState = this.createInitialRunState(
      {
        recordingId,
        noteId,
        capturedAudioId,
        engine,
        isRecordingWithoutTranscription,
        deviceInfo,
      },
      now
    );

    await this.repository.saveCapturedAudioId({
      noteId: runState.noteId,
      recordingId: runState.recordingId,
      capturedAudioId: runState.capturedAudioId,
      createdAt: now,
    });

    await this.repository.saveRecordingMetadata({ run: runState, createdAt: now });

    return this.loadRecording({ recordingId: runState.recordingId });
  }

  async markRecording({ recordingId }: RecordingIdentifier): Promise<void> {
    await this.updateRecording({ recordingId }, run => ({
      status: "RECORDING",
      startedAt: run.startedAt ?? this.deps.now(),
      lastResumedAt: this.deps.now(),
    }));
  }

  async markPaused({ recordingId, progress }: RecordingProgressArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({
      status: "PAUSED",
      lastResumedAt: null,
      totalCapturedMs: progress.totalCapturedMs,
      pausedAccumulatedMs: progress.pausedAccumulatedMs ?? progress.totalCapturedMs,
    }));
  }

  async markResumed({ recordingId, progress }: RecordingProgressArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({
      status: "RECORDING",
      lastResumedAt: this.deps.now(),
      totalCapturedMs: progress.totalCapturedMs,
      pausedAccumulatedMs: progress.pausedAccumulatedMs ?? 0,
    }));
  }

  async markStopping({ recordingId }: RecordingIdentifier): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({ status: "STOPPING" }));
  }

  async markStopped({
    recordingId,
    progress,
  }: {
    recordingId: string;
    progress?: AudioRecordingProgressUpdate;
  }): Promise<void> {
    await this.updateRecording({ recordingId }, run => ({
      status: "STOPPED",
      totalCapturedMs: progress?.totalCapturedMs ?? run.totalCapturedMs,
      pausedAccumulatedMs: progress?.pausedAccumulatedMs ?? run.pausedAccumulatedMs,
    }));
  }

  async updateRecordingProgress({ recordingId, progress }: RecordingProgressArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({
      totalCapturedMs: progress.totalCapturedMs,
      pausedAccumulatedMs: progress.pausedAccumulatedMs,
    }));
  }

  async updateSilence({ recordingId, silenceSince }: UpdateSilenceArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({ silenceSince }));
  }

  async setAutoStopReason({ recordingId, reason }: SetAutoStopReasonArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({ autoStopReason: reason }));
  }

  async updateDeviceInfo({ recordingId, deviceInfo }: UpdateDeviceInfoArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({ deviceInfo }));
  }

  async updateUploadState({ recordingId, update }: UpdateUploadStateArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({
      upload: {
        state: update.state,
        progressPercent: update.progressPercent ?? null,
        error: update.error ?? null,
        updatedAt: this.deps.now(),
      },
    }));
  }

  async updateTransformState({ recordingId, update }: UpdateTransformStateArgs): Promise<void> {
    await this.updateRecording({ recordingId }, () => ({
      transform: {
        state: update.state,
        error: update.error ?? null,
        updatedAt: this.deps.now(),
      },
    }));
  }

  async listRuns({ limit }: { limit?: number } = {}) {
    const runs = await this.repository.listRecordingMetadata({ limit });
    return runs;
  }

  async appendAudioChunk({ recordingId, blob }: AppendAudioChunkArgs): Promise<void> {
    const run = await this.requireRun(recordingId);
    await this.repository.appendAudioBlob({
      noteId: run.noteId,
      recordingId,
      blobData: blob,
    });
  }

  async appendTranscriptTurn({
    recordingId,
    channel,
    transcript,
  }: AppendTranscriptTurnArgs): Promise<void> {
    const run = await this.requireRun(recordingId);
    await this.repository.appendTranscriptChunk({
      noteId: run.noteId,
      recordingId,
      transcript,
      channel,
    });
  }

  async appendTranscriptSnapshot({
    recordingId,
    channel,
    transcripts,
    snapshotTime,
  }: AppendTranscriptSnapshotArgs): Promise<void> {
    const run = await this.requireRun(recordingId);
    await this.repository.appendTranscriptSnapshot({
      noteId: run.noteId,
      recordingId,
      transcripts,
      snapshotTime,
      channel,
    });
  }

  async clearRecording({ recordingId }: RecordingIdentifier): Promise<void> {
    await this.repository.softDeleteByRecordingId(recordingId);
  }

  async loadRecording({
    recordingId,
  }: RecordingIdentifier): Promise<AudioRecordingSnapshot | null> {
    const state = await this.repository.getRunRecordingState({ recordingId });
    if (!state?.metadata) {
      return null;
    }

    return state as AudioRecordingSnapshot;
  }

  async loadActiveRecording({
    noteId,
  }: {
    noteId: string;
  }): Promise<AudioRecordingSnapshot | null> {
    const state = await this.repository.getRecordingState({ noteId });
    if (!state?.metadata) {
      return null;
    }

    return state as AudioRecordingSnapshot;
  }

  async hasRecordingForNote({ noteId }: { noteId: string }): Promise<boolean> {
    return this.repository.hasAnyForNote({ noteId });
  }

  observeRecording({
    recordingId,
    observer,
  }: {
    recordingId: string;
    observer: AudioRecordingObserver;
  }): Subscription {
    return liveQuery(() => this.loadRecording({ recordingId })).subscribe({
      next: snapshot => observer.next(snapshot),
      error: error => observer.error?.(error),
    });
  }

  private async updateRecording(
    { recordingId }: RecordingIdentifier,
    deriveUpdate: (
      run: AudioRecordingMetadata
    ) => Partial<AudioRecordingMetadata> & { status?: AudioRecordingStatus }
  ): Promise<void> {
    const current = await this.requireRun(recordingId);
    const now = this.deps.now();
    const updates = deriveUpdate(current);

    const status = updates.status ?? current.status;
    const { upload, transform, ...rest } = updates;
    const next: AudioRecordingMetadata = {
      ...current,
      ...rest,
      status,
      lastUpdatedAt: now,
      upload: upload ? { ...current.upload, ...upload } : current.upload,
      transform: transform ? { ...current.transform, ...transform } : current.transform,
    };

    await this.repository.saveRecordingMetadata({ run: next, createdAt: current.createdAt });
  }

  private async requireRun(recordingId: string): Promise<AudioRecordingMetadata> {
    const run = await this.repository.getRecordingMetadata({ recordingId });
    if (!run) {
      throw new Error(`Recording ${recordingId} not found`);
    }
    return run;
  }

  private createInitialRunState(
    {
      recordingId,
      noteId,
      capturedAudioId,
      engine,
      isRecordingWithoutTranscription,
      deviceInfo,
    }: StartAudioRecordingCommand,
    timestamp: number
  ): AudioRecordingMetadata {
    return {
      recordingId,
      noteId,
      capturedAudioId,
      engine,
      status: "INITIALIZING",
      createdAt: timestamp,
      startedAt: null,
      lastUpdatedAt: timestamp,
      lastResumedAt: null,
      pausedAccumulatedMs: 0,
      totalCapturedMs: 0,
      silenceSince: null,
      autoStopReason: null,
      isRecordingWithoutTranscription: isRecordingWithoutTranscription ?? false,
      deviceInfo: deviceInfo ?? createDefaultDeviceInfo(),
      upload: createDefaultUploadState(timestamp),
      transform: createDefaultTransformState(timestamp),
      softDeletedAt: null,
    };
  }
}
